/**
 * Voice notes: MediaRecorder with the right container per browser, plus an input-level meter
 * (Web Audio analyser) for the recording mark. No React here; the Composer drives it.
 */
import { iosMajorVersion, isIos, isStandalone } from './viewport';

/** Recording containers in preference order; iOS Safari and the Home Screen app take audio/mp4. */
const CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

export function pickRecorderMime(
  isSupported: (mime: string) => boolean = (m) =>
    typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(m),
): string | undefined {
  return CANDIDATES.find((m) => isSupported(m));
}

/** Minimum iOS for the standalone (Home Screen) app to record reliably. */
export const MIN_IOS_STANDALONE = 17;

export type RecorderSupport = 'ok' | 'unsupported' | 'ios_too_old';

export function recorderSupport(): RecorderSupport {
  if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return 'unsupported';
  }
  if (isIos() && isStandalone()) {
    const v = iosMajorVersion();
    if (v !== undefined && v < MIN_IOS_STANDALONE) return 'ios_too_old';
  }
  return pickRecorderMime() ? 'ok' : 'unsupported';
}

export interface Recording {
  blob: Blob;
  mime: string;
  duration_ms: number;
}

export interface VoiceRecorder {
  /** Current input level 0..1 (RMS, smoothed), read on each animation frame. */
  readonly level: number;
  readonly startedAt: number;
  stop(): Promise<Recording>;
  cancel(): void;
}

export async function startRecording(): Promise<VoiceRecorder> {
  const mime = pickRecorderMime();
  if (!mime) throw new Error('no supported recorder mime');
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true },
  });
  const recorder = new MediaRecorder(stream, { mimeType: mime });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };

  // Level meter.
  let context: AudioContext | undefined;
  let analyser: AnalyserNode | undefined;
  let data: Uint8Array<ArrayBuffer> | undefined;
  try {
    context = new AudioContext();
    const source = context.createMediaStreamSource(stream);
    analyser = context.createAnalyser();
    analyser.fftSize = 512;
    source.connect(analyser);
    data = new Uint8Array(new ArrayBuffer(analyser.fftSize));
  } catch {
    // no meter; recording still works
  }
  let level = 0;
  let frame = 0;
  const tick = () => {
    if (analyser && data) {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (const sample of data) {
        const v = (sample - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      level = level * 0.7 + Math.min(1, rms * 3) * 0.3;
    }
    frame = requestAnimationFrame(tick);
  };
  frame = requestAnimationFrame(tick);

  const startedAt = Date.now();
  recorder.start(250);

  const release = () => {
    cancelAnimationFrame(frame);
    stream.getTracks().forEach((t) => t.stop());
    void context?.close().catch(() => undefined);
  };

  return {
    get level() {
      return level;
    },
    startedAt,
    stop: () =>
      new Promise<Recording>((resolve, reject) => {
        recorder.onstop = () => {
          release();
          const blob = new Blob(chunks, { type: mime.split(';')[0] ?? mime });
          if (blob.size === 0) {
            reject(new Error('empty recording'));
            return;
          }
          resolve({ blob, mime: blob.type || mime, duration_ms: Date.now() - startedAt });
        };
        recorder.onerror = () => {
          release();
          reject(new Error('recording failed'));
        };
        if (recorder.state !== 'inactive') recorder.stop();
        else recorder.onstop(new Event('stop'));
      }),
    cancel: () => {
      recorder.onstop = null;
      if (recorder.state !== 'inactive') recorder.stop();
      release();
    },
  };
}

/** "0:07" style timer text. */
export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
